import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import QrCodeSVGComponent from 'Components/common/QRCodeComponents/QrCodeSVGComponent';
import {QR_CODE_SOURCES} from 'Utils/analytics';
import {INSIGHTS_METADATA, ROUTE_OPTIONS} from 'Utils/constants';
import FeaturePropType from 'Utils/propTypes/FeaturePropType';
import getExplorerUrl from 'Utils/getExplorerUrl';
import {generateRouteParams} from 'Utils/routingUtils';
import getShortUrl from 'Data/services/getShortUrl';
import 'Components/layouts/kiosk/KioskLayout/LayoutComponents/scss/kiosk.scss';
import QrCodePlaceholder from './QrCodePlaceholder';

const DirectionsOrExternalURLQRCodeComponent = ({
    autoFillHeightWidth,
    bgColor,
    currentKioskFeature,
    experience,
    fgColor,
    isSearchingRoute,
    location,
    pathFound,
    nightSafePathSearch,
    prompt,
    selectedRouteOption,
    showCalculatingRouteFlag,
    style,
    type,
    qrCodeConfig,
}) => {
    const [qrCodeShortUrl, setQrCodeShortUrl] = useState(null);

    useEffect(
        () => {
            let mounted = true;
            const updateQrCode = (feature, routeOptions) => {
                const params = feature
                    ? {
                          ...generateRouteParams({
                              start: currentKioskFeature,
                              end: feature,
                              routeOptions,
                          }),
                          fromQr: QR_CODE_SOURCES.DIGITAL_SIGN,
                          destinationType: type,
                          scanLocation: currentKioskFeature.properties.locationId,
                          ...(qrCodeConfig?.mapStyle && {mapTheme: qrCodeConfig?.mapStyle}),
                          ...(qrCodeConfig?.zoom && {zoom: qrCodeConfig?.zoom}),
                          ...(qrCodeConfig?.pitch && {pitch: qrCodeConfig?.pitch}),
                      }
                    : {};

                setQrCodeShortUrl(null);

                if (pathFound) {
                    const metadata = {
                        type: INSIGHTS_METADATA.TYPES.ROUTE,
                        typeId: feature.properties.locationId,
                        action: INSIGHTS_METADATA.ACTIONS.SCAN,
                        sourceDeviceId: currentKioskFeature.properties.featureId,
                    };
                    getExplorerUrl(params, metadata).then(newQrCodeShortUrl => {
                        // only change state if the component is mounted
                        if (mounted) {
                            setQrCodeShortUrl(newQrCodeShortUrl);
                        }
                    });
                } else if (experience?.url) {
                    const metadata = {
                        type: INSIGHTS_METADATA.TYPES.EXPERIENCES,
                        typeId: experience.id,
                        action: INSIGHTS_METADATA.ACTIONS.SCAN,
                        sourceDeviceId: currentKioskFeature.properties.featureId,
                    };
                    getShortUrl(experience.url, metadata).then(shortUrl =>
                        setQrCodeShortUrl(shortUrl)
                    );
                }
            };

            updateQrCode(location, selectedRouteOption);

            // cleanup function
            return () => {
                mounted = false;
            };
        },
        [location, pathFound, selectedRouteOption, experience]
    );

    const showQrCode =
        (pathFound && !isSearchingRoute && !!qrCodeShortUrl) ||
        (!!qrCodeShortUrl && !!experience?.url);
    const showNoRouteFlag = !pathFound && !isSearchingRoute && !experience?.url;
    const _showCalculatingRouteFlag = !showQrCode && !showNoRouteFlag && showCalculatingRouteFlag;
    const qrCodePrompt = prompt || 'Scan for directions';

    return (
        <div id="kiosk-directions-qr">
            {showQrCode && (
                <>
                    <QrCodeSVGComponent
                        autoFillHeightWidth={autoFillHeightWidth}
                        size={84}
                        value={qrCodeShortUrl}
                        style={style}
                        bgColor={bgColor}
                        fgColor={fgColor}
                    />
                    <span>{qrCodePrompt}</span>
                </>
            )}
            {showNoRouteFlag &&
                (nightSafePathSearch ? (
                    <p>Sorry, a night safe route is not available between these locations.</p>
                ) : (
                    <p>Could not find a route between the selected locations</p>
                ))}
            {_showCalculatingRouteFlag && (
                <>
                    <QrCodePlaceholder />
                    <span>Calculating route...</span>
                </>
            )}
        </div>
    );
};

DirectionsOrExternalURLQRCodeComponent.defaultProps = {
    autoFillHeightWidth: false,
    showCalculatingRouteFlag: true,
};

DirectionsOrExternalURLQRCodeComponent.propTypes = {
    autoFillHeightWidth: PropTypes.bool,
    bgColor: PropTypes.string,
    currentKioskFeature: FeaturePropType,

    experience: PropTypes.object,
    fgColor: PropTypes.string,
    isSearchingRoute: PropTypes.bool.isRequired,
    location: PropTypes.object,
    pathFound: PropTypes.bool,
    nightSafePathSearch: PropTypes.bool,
    prompt: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    selectedRouteOption: PropTypes.oneOf(Object.values(ROUTE_OPTIONS)).isRequired,
    showCalculatingRouteFlag: PropTypes.bool,
    style: PropTypes.object,
    type: PropTypes.string,
    qrCodeConfig: PropTypes.object,
};

export default DirectionsOrExternalURLQRCodeComponent;
